import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { Toast, ToastProviderPrimitive, ToastViewport, type ToastTone } from "@/components/ui/toast";

type ToastEntry = {
  id: number;
  tone: ToastTone;
  message: string;
};

type ToastContextValue = {
  error: (message: string) => void;
  success: (message: string) => void;
  info: (message: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

/** Long enough to read a sentence, short enough not to sit on the tab bar. */
const DURATION_MS = 6000;

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((tone: ToastTone, message: string) => {
    if (!message) return;
    setToasts((prev) => {
      // The same message twice in a row reads as a glitch, not as two events.
      if (prev.some((toast) => toast.message === message && toast.tone === tone)) return prev;
      return [...prev, { id: nextId++, tone, message }];
    });
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      error: (message) => push("error", message),
      success: (message) => push("success", message),
      info: (message) => push("info", message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      <ToastProviderPrimitive duration={DURATION_MS} swipeDirection="right">
        {children}
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            tone={toast.tone}
            message={toast.message}
            onOpenChange={(open) => {
              if (!open) dismiss(toast.id);
            }}
          />
        ))}
        <ToastViewport />
      </ToastProviderPrimitive>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used inside a ToastProvider");
  }
  return context;
}
